import { sparkToast, SparkModalPortal, Modal as SparkModal, ModalFeedback, SparkIcon, Checkbox as SparkCheckbox, Button as SparkButton, TooltipTrigger, TagColored, PopoverMenu, Skeleton, InputTextArea, useReadOnly } from '@spark';
import './FullTextModal.css';
import { colors } from "@/shared/tokens/colors"
import ReviewStatusIndicator from "@/features/datasets/components/DataSelection/ReviewStatusIndicator.jsx";
import debounce from 'lodash/debounce';
import { ActivityType, DatasetOrigin, TaskType, isGenerativeTask, getGenerativeOutputNoun } from "@/shared/domain/taskTypes";
import { data_from_server_post } from "@/shared/http/client";
import { handleCopy } from "@/shared/utils/helpers";
import LabelSelector from "@/features/tasks/components/LabelSelector";
import NERTagger from '@/features/datasets/components/DataSelection/NERTagger';
import NerAnnotateEditor from '@/features/datasets/components/DataSelection/NerAnnotateEditor';
import { updateRecordResponse } from '@/features/datasets/api/exampleUpdates';
import { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { getTextDirectionStyle } from "@/shared/utils/textUtils";
import { ValidationDetails } from "@/features/datasets/components/DataSelection/ValidationDetails.jsx";

/** Inline replacement for the deleted EllipsisTagWithTooltip design-system component. */
const EllipsisTagWithTooltip = ({ text, maxWidth = 300, variant, style = {}, noMargin = false, ...props }) => {
    const [isTruncated, setIsTruncated] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        setIsTruncated(el.scrollWidth > el.clientWidth);
    }, [text, maxWidth, style]);
    const tag = (
        <span ref={ref} {...props} style={{ maxWidth, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block', verticalAlign: 'middle', marginBottom: noMargin ? 0 : '5px', ...style }}>
            <TagColored variant={variant} label={text} />
        </span>
    );
    return isTruncated ? <TooltipTrigger text={text} placement="top">{tag}</TooltipTrigger> : tag;
};
import { getExtraParamValue, getExtraParamRaw, getExtraParamEntities, tokenizeLabels, countByLabel, formatOriginLabelsColumnLabel, extraParamColumnMinWidth, ORIGIN_LABELS_COLUMN_RE } from '@/features/datasets/components/DataSelection/ExtraParamsUtils';
import { EntitiesPreviewCell } from '@/features/datasets/components/DataSelection/EntitiesPreviewCell.jsx';
import { LabelsPreviewCell } from '@/features/datasets/components/DataSelection/LabelsPreviewCell.jsx';
import { getUserFacingDatasetName, resolveDatasetNameForRecord } from "@/features/datasets/utils/datasetCloneNames";
import { originLabelsFilterKey, originLabelsFilterPayload } from '@/features/datasets/utils/formatFiltersForAPI';
import { getSparkTagVariant } from '@/features/datasets/components/DataSelection/sparkLabelColors';
import { isAiLabelOverridden } from '@/features/tasks/utils/labelConfidenceTooltip';
import { isAgenticAutonomyLevel } from '@/features/tasks/components/CreationWizard/businessGoals/autonomyLevels';
import { AgenticExampleCell } from '@/features/datasets/components/DataSelection/agentic/AgenticExampleCell.jsx';
import { ToolsCalledCell } from '@/features/datasets/components/DataSelection/agentic/ToolsCalledCell.jsx';
import { tabToolNames, toolTagVariantsByName } from '@/features/datasets/components/DataSelection/agentic/trajectoryView';
import {
    CheckboxFilterPanel,
    ColumnFilterHeader,
    DATA_CREATION_TYPE_FILTERS,
    LabelsFilterPanel,
    OriginLabelsFilterPanel,
    ReviewedFilterPanel,
    ToolsCalledFilterPanel,
    uniqueDatasetFilterNames,
} from '@/features/datasets/components/DataSelection/DatasetTableColumnFilters';

/** Inline replacement for the deleted EditableText design-system component. */
const EditableText = ({ text, onSave, style = {}, className = '', endIcon }) => {
    const { readOnly } = useReadOnly();
    const [editedText, setEditedText] = useState(text);
    const funcRef = useRef();
    const debouncedSave = useRef(debounce((...args) => funcRef.current?.(...args), 1500)).current;
    funcRef.current = async (value) => {
        try {
            const result = onSave(value);
            if (result && typeof result.then === 'function') await result;
        } catch {
            setEditedText(text);
        }
    };
    const dirStyle = getTextDirectionStyle(editedText);
    return (
        <InputTextArea
            defaultValue={text}
            showLabel={false}
            showHelperText={false}
            placeholder=""
            stretch
            className={`editable-text-spark ${className}`}
            style={{ ...dirStyle, maxWidth: '100%', ...style }}
            endIcon={endIcon}
            disabled={readOnly}
            onChange={(e) => {
                setEditedText(e.target.value);
                debouncedSave(e.target.value);
            }}
        />
    );
};

const DEFAULT_SCORE = -999
const AI_FULLY_GENERATED_ORIGIN = DatasetOrigin.specific_ai_fully_generated;
const AI_LABELED_ORIGIN = DatasetOrigin.specific_ai_labeled;
const AI_GENERATED_TOOLTIP = 'AI Generated';

function AiGeneratedExampleIcon() {
    return (
        <TooltipTrigger text={AI_GENERATED_TOOLTIP} placement="top">
            <span role="img" aria-label={AI_GENERATED_TOOLTIP}>
                <SparkIcon slug="sparkles-2" size="medium" />
            </span>
        </TooltipTrigger>
    );
}

const normalizeLabels = (labels) => {
    if (!Array.isArray(labels)) return [];
    return labels.map((l) => (l ?? '').toString().trim()).filter(Boolean);
};

const hasEditedExample = (record) => {
    const current = record?.request_example;
    const origin = record?.origin_request_example;
    // Only consider "edited" when we actually have an origin value to compare against.
    if (origin === undefined || origin === null) return false;
    return (current ?? '') !== (origin ?? '');
};

export const ScoreQualityTH = {
    HIGH: 70,
    MEDIUM: 50,
}

// Moved to the api layer so hooks (e.g. useNerAnnotationAutoSave) can call it
// without importing this component module; re-exported for existing consumers.
export { updateRecordResponse };

function updateRecordLabels(record, newText, dataset, isBenchmark, updateRecord, llm_usecase_id, fetchValidationCounts, onDataOverviewRefresh) {
    return new Promise((resolve, reject) => {
        const new_response = {
            labels: newText
        }

        data_from_server_post(
            "single-example/update-response",
            {
                client_id: record.client_id,
                dataset: dataset,
                is_benchmark: isBenchmark,
                new_response: new_response,
                is_document_reviewed: true,
                request_example: record.request_example,
                llm_usecase_id: llm_usecase_id,
            },
            (result) => {
                if (result?.success) {

                    // After successful update, refresh the data for current page
                    if (updateRecord) {
                        updateRecord(result.new_record, isBenchmark);
                    }

                    // If fetchValidationCounts is provided, call it to update validation counts
                    if (fetchValidationCounts) {
                        fetchValidationCounts();
                    }
                    onDataOverviewRefresh?.();
                    resolve(result);
                } else {
                    sparkToast.error(`Failed to update labels`);
                    reject(new Error('Failed to update labels'));
                }
            }
        );
    });
}

function updateRecordExample(record, newText, dataset, isBenchmark, updateRecord, fetchValidationCounts, llm_usecase_id, onDataOverviewRefresh) {
    return new Promise((resolve, reject) => {
        data_from_server_post(
            "single-example/update-example",
            {
                client_id: record.client_id,
                dataset: dataset,
                is_benchmark: isBenchmark,
                new_field_value: newText,
                is_validated: true,
                request_example: record.request_example,
                request_example_metadata: null,
                llm_usecase_id: llm_usecase_id ?? null,
            },
            (result) => {
                if (result?.success) {
                    // After successful update, refresh the data for current page
                    if (updateRecord) {
                        updateRecord(result.new_record, isBenchmark);
                    }

                    // If fetchValidationCounts is provided, call it to update validation counts
                    if (fetchValidationCounts) {
                        fetchValidationCounts();
                    }
                    // Content changed: let the task page re-check its change
                    // status (Retrain / Revaluate / Deploy primary button).
                    onDataOverviewRefresh?.();
                    resolve(result);
                } else {
                    sparkToast.error(`Failed to update example field`);
                    reject(new Error('Failed to update example field'));
                }
            }
        );
    });
}

function validateAllEntities(record, dataset, isBenchmark, updateRecord, fetchValidationCounts, llm_usecase_id, taskType) {
    const updatedEntities = (record?.response?.entities || []).map(e => ({ ...e, is_validated: true }));
    const newResponse = { ...record.response, entities: updatedEntities };
    updateRecordResponse(record, newResponse, dataset, isBenchmark, updateRecord, fetchValidationCounts, llm_usecase_id, null, null, taskType);
}

export function FullTextModal({ visible, onClose, record, entityOptions, onChange, datasetName, refreshCategories, client_id, relationshipTypes, refreshRelationshipTypes, updateRecord, fetchValidationCounts, llm_usecase_id, isBenchmark, taskType, onRefreshRecords, onDataOverviewRefresh }) {
    if (!record) return null;

    // Plain NER gets the editable annotate body (shared NerExampleEditor with
    // autosave); NER with relations keeps the legacy tagger, whose relations
    // reference entities by start_index and don't survive free text edits.
    const isEditableNer = taskType === TaskType.NER;

    return (
        <SparkModalPortal
            open={visible}
            onClose={onClose}
            destroyOnClose
            size="wide"
            className="ner-annotate-portal"
        >
            <SparkModal
                className="ner-annotate-modal"
                title="Annotate"
                subtitle={isEditableNer
                    ? 'Highlight one or more words and select an entity type to label them'
                    : 'Mark the text or select Entity Tag to start Tagging'}
                showSubtitle
                showScrollBar={false}
                showFooter={false}
                showClose
                onCloseClick={onClose}
            >
                {isEditableNer ? (
                    <NerAnnotateEditor
                        record={record}
                        entityOptions={entityOptions}
                        datasetName={datasetName}
                        refreshCategories={refreshCategories}
                        updateRecord={updateRecord}
                        fetchValidationCounts={fetchValidationCounts}
                        llm_usecase_id={llm_usecase_id}
                        isBenchmark={isBenchmark}
                        taskType={taskType}
                        onRefreshRecords={onRefreshRecords}
                        onDataOverviewRefresh={onDataOverviewRefresh}
                    />
                ) : (
                    <NERTagger
                        text={record.request_example}
                        entities={record?.response?.entities || []}
                        entityOptions={entityOptions}
                        onChange={(newEntities, newRelations, activity_type, activity_comment) => {
                            onChange(record, newEntities, newRelations, activity_type, activity_comment);
                        }}
                        isFullScreen={true}
                        relationships={record?.response?.relations || []}
                        dataset={datasetName}
                        refreshCategories={refreshCategories}
                        relationshipMode={false}
                        client_id={client_id}
                        relationshipTypes={relationshipTypes}
                        refreshRelationshipTypes={refreshRelationshipTypes}
                        recordId={record._id}
                        updateRecord={updateRecord}
                        llm_usecase_id={llm_usecase_id}
                        onRefreshRecords={onRefreshRecords}
                        fetchValidationCounts={fetchValidationCounts}
                        isBenchmark={isBenchmark}
                    />
                )}
            </SparkModal>
        </SparkModalPortal>
    );
}

// Create a new component for the NER response cell
function NERResponseCell({ text, record, entityOptions, onChange, datasetName, refreshCategories, isBenchmark, updateRecord, fetchValidationCounts, client_id, relationshipTypes, refreshRelationshipTypes, llm_usecase_id, taskType, onRefreshRecords, onDataOverviewRefresh, endIcon }) {
    const [showFullTextModal, setShowFullTextModal] = useState(false);

    return (
        <>
            <div style={{ position: 'relative' }}>
                <InputTextArea
                    value={text}
                    readOnly
                    showLabel={false}
                    showHelperText={false}
                    placeholder=""
                    className="spark-ner-preview-textarea"
                    style={{ ...getTextDirectionStyle(text), maxWidth: '100%' }}
                    endIcon={endIcon}
                />
            </div>
            <FullTextModal
                visible={showFullTextModal}
                onClose={() => {
                    setShowFullTextModal(false);
                    // Remove recordId from URL when closing from NER cell
                    const urlParams = new URLSearchParams(window.location.search);
                    urlParams.delete('recordId');
                    const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
                    window.history.replaceState({}, '', newUrl);
                }}
                record={record}
                entityOptions={entityOptions}
                onChange={onChange}
                datasetName={datasetName}
                refreshCategories={refreshCategories}
                client_id={client_id}
                relationshipTypes={relationshipTypes}
                refreshRelationshipTypes={refreshRelationshipTypes}
                updateRecord={updateRecord}
                fetchValidationCounts={fetchValidationCounts}
                llm_usecase_id={llm_usecase_id}
                isBenchmark={isBenchmark}
                taskType={taskType}
                onRefreshRecords={onRefreshRecords}
                onDataOverviewRefresh={onDataOverviewRefresh}
            />
        </>
    );
}

// Create modular functions for reusable columns
/**
 * Creates a Reviewed column that displays clickable review status indicators
 * Shows green checkmark for reviewed items and empty circle for unreviewed items
 * Users can click to toggle the review status
 */
function createReviewedColumn(client_id, firstDatasetOrBenchmark, isBenchmark, llm_usecase_id, updateRecord, fetchValidationCounts, currentFilters = {}, currentUsecase = null, onColumnFiltersChange = null, onTablePopoverChange = null, onDataOverviewRefresh = null) {
    const reviewFilter = currentFilters.review_status || null;
    return {
        title: (
            <ColumnFilterHeader
                title="Reviewed"
                active={!!reviewFilter}
                onOpenChange={(open) => onTablePopoverChange?.('reviewed', open)}
            >
                {({ close }) => (
                    <ReviewedFilterPanel
                        selectedKey={reviewFilter}
                        onClose={close}
                        onClear={() => onColumnFiltersChange?.({ review_status: null })}
                        onApply={(value) => onColumnFiltersChange?.({ review_status: value })}
                    />
                )}
            </ColumnFilterHeader>
        ),
        key: 'review',
        width: '160px',
        render: (text, record) => (
            <ReviewStatusIndicator
                record={record}
                client_id={client_id}
                firstDatasetOrBenchmark={firstDatasetOrBenchmark}
                currentUsecase={currentUsecase}
                isBenchmark={isBenchmark}
                llm_usecase_id={llm_usecase_id}
                updateRecord={updateRecord}
                fetchValidationCounts={fetchValidationCounts}
                onDataOverviewRefresh={onDataOverviewRefresh}
            />
        ),
    };
}

function MoreActionsCell({ record, client_id, llm_usecase_id, isBenchmark, fetchValidationCounts, currentFilters, onRefreshRecords, onDataOverviewRefresh, taskType }) {
    const [open, setOpen] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const btnRef = useRef(null);
    const menuRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const handleOutsideClick = (e) => {
            if (menuRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
            setOpen(false);
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [open]);

    const isArchived = currentFilters?.is_in_dataset === false || record?.is_in_dataset === false;

    const handleCopyToClipboard = () => {
        handleCopy(record.request_example);
        setOpen(false);
    };

    const handleCopySummary = () => {
        handleCopy(record.response?.response);
        setOpen(false);
    };

    const handleArchiveOrUnarchiveRecord = () => {
        setOpen(false);
        setConfirmOpen(true);
    };

    const executeArchiveOperation = () => {
        const operationType = isArchived ? "unarchive" : "archive";
        data_from_server_post(
            "bulk-operations",
            {
                client_id,
                llm_usecase_id,
                is_benchmark: isBenchmark,
                operation_type: operationType,
                also_null_responses: false,
                also_null_request_examples: false,
                is_select_all: false,
                included_ids: [record?._id],
            },
            (result) => {
                if (result?.success) {
                    sparkToast.success(`Successfully ${isArchived ? "unarchived" : "archived"} record`);
                    if (fetchValidationCounts) fetchValidationCounts();
                    if (typeof onRefreshRecords === "function") onRefreshRecords();
                    onDataOverviewRefresh?.();
                } else {
                    sparkToast.error(result?.error || `Failed to ${isArchived ? "unarchive" : "archive"} record`);
                }
            },
            () => {
                sparkToast.error(`Failed to ${isArchived ? "unarchive" : "archive"} record`);
            }
        );
        setConfirmOpen(false);
    };

    const menuItems = [
        { label: 'Copy Example', showPrefixIcon: true, prefixIconSlug: 'copy', onClick: handleCopyToClipboard },
        ...(taskType === TaskType.summarization
            ? [{ label: 'Copy Summary', showPrefixIcon: true, prefixIconSlug: 'copy', onClick: handleCopySummary }]
            : []),
        { label: isArchived ? 'Unarchive' : 'Archive', showPrefixIcon: true, prefixIconSlug: 'trash', onClick: handleArchiveOrUnarchiveRecord, disableWhenReadOnly: true },
    ];

    return (
        <div ref={btnRef} style={{ position: 'relative', display: 'inline-flex' }}>
            <button
                type="button"
                className="spark-btn spark-btn-tiny spark-btn-tertiary spark-btn-icon-only"
                aria-label="More actions"
                onClick={() => setOpen(prev => !prev)}
            >
                <span className="spark-btn-icon"><SparkIcon slug="dots" size="small" /></span>
            </button>
            {open && createPortal(
                <MoreActionsPortaledMenu btnRef={btnRef} menuRef={menuRef} items={menuItems} />,
                document.body
            )}
            <SparkModalPortal
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                destroyOnClose
                size="text"
            >
                <ModalFeedback
                    feedbackType="warning"
                    title={isArchived ? "Unarchive this record?" : "Archive this record?"}
                    content={isArchived
                        ? "This will move the record back to the active dataset/benchmark."
                        : "This will move the record to Archived datasets/benchmarks."}
                    showClose
                    showOverlay={false}
                    danger={!isArchived}
                    primaryLabel={isArchived ? "Unarchive" : "Archive"}
                    primaryDisableWhenReadOnly
                    tertiaryLabel="Cancel"
                    onCloseClick={() => setConfirmOpen(false)}
                    onPrimaryClick={executeArchiveOperation}
                    onTertiaryClick={() => setConfirmOpen(false)}
                />
            </SparkModalPortal>
        </div>
    );
}

function MoreActionsPortaledMenu({ btnRef, menuRef, items }) {
    const [pos, setPos] = useState({ top: 0, left: 0 });

    useLayoutEffect(() => {
        if (!btnRef.current) return;
        const rect = btnRef.current.getBoundingClientRect();
        setPos({
            top: rect.bottom + window.scrollY + 4,
            left: rect.right + window.scrollX - 130,
        });
    }, [btnRef]);

    return (
        <div
            ref={menuRef}
            style={{ position: 'absolute', top: pos.top, left: pos.left, zIndex: 1050 }}
        >
            <PopoverMenu tip={false} items={items} className="spark-dataset-actions-menu" />
        </div>
    );
}

function createMoreActionsColumn(
    client_id,
    firstDatasetOrBenchmark,
    isBenchmark,
    llm_usecase_id,
    updateRecord,
    fetchValidationCounts,
    currentFilters = {},
    onRefreshRecords = null,
    onDataOverviewRefresh = null,
    taskType = null
) {
    return {
        title: '',
        key: 'moreActions',
        width: '1%',
        render: (text, record) => (
            <MoreActionsCell
                record={record}
                client_id={client_id}
                llm_usecase_id={llm_usecase_id}
                isBenchmark={isBenchmark}
                fetchValidationCounts={fetchValidationCounts}
                currentFilters={currentFilters}
                onRefreshRecords={onRefreshRecords}
                onDataOverviewRefresh={onDataOverviewRefresh}
                taskType={taskType}
            />
        ),
    };
}

function createViewColumn(extraProps) {
    return {
        title: '',
        key: 'view',
        width: 80,
        render: (text, record) => (
            <SparkButton
                size="tiny"
                variant="ghost"
                onClick={() => {
                    if (extraProps.setSelectedRecord) extraProps.setSelectedRecord(record);
                    if (extraProps.setShowFullTextModal) extraProps.setShowFullTextModal(true);

                    const urlParams = new URLSearchParams(window.location.search);
                    urlParams.set('recordId', record._id);
                    const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
                    window.history.replaceState({}, '', newUrl);
                }}
            >
                View
            </SparkButton>
        ),
    };
}

function createExtraParamsColumns(
    extra_params_titles,
    crossReferenceFields = {},
    dataSource = [],
    allLabelOptions = [],
    isMultilabel = false,
    currentFilters = {},
    datasetNames = [],
    onColumnFiltersChange = null,
    enableOriginLabelsFilter = false,
    onTablePopoverChange = null,
    datasetCardValues = [],
) {
    const LONG_TEXT_THRESHOLD = 150;
    const sampleOriginOptions = uniqueDatasetFilterNames(datasetNames).map((name) => ({
        label: getUserFacingDatasetName(name) || name,
        value: name,
    }));
    // Distinct dataset_card values (public hub ids stamped by the Retrieval
    // Agent), fetched server-side; same checkbox filter design as Sample Origin.
    const datasetCardOptions = (datasetCardValues || []).map((value) => ({
        label: value,
        value,
    }));

    const longTextColumns = new Set(
        extra_params_titles.filter(title => {
            if (title === 'data_creation_type' || title === 'dataset') return false;
            return dataSource.some(record => {
                const value = getExtraParamValue(record, title);
                return typeof value === 'string' && value.length > LONG_TEXT_THRESHOLD;
            });
        })
    );

    return extra_params_titles.map(title => {
        let displayTitle = formatOriginLabelsColumnLabel(title);
        let width = undefined;
        const isOriginLabelsColumn = ORIGIN_LABELS_COLUMN_RE.test(title);
        const hasFilter = title === 'data_creation_type'
            || title === 'dataset'
            || title === 'dataset_card'
            || (enableOriginLabelsFilter && isOriginLabelsColumn);

        const crossRefConfig = crossReferenceFields?.[title] || null;

        if (title === 'data_creation_type') {
            const selected = currentFilters.data_creation_type || [];
            displayTitle = (
                <ColumnFilterHeader
                    title="Data Creation"
                    active={selected.length > 0}
                    onOpenChange={(open) => onTablePopoverChange?.('data-creation', open)}
                >
                    {({ close }) => (
                        <CheckboxFilterPanel
                            options={DATA_CREATION_TYPE_FILTERS}
                            selectedKeys={selected}
                            onClose={close}
                            onClear={() => onColumnFiltersChange?.({ data_creation_type: null })}
                            onApply={(keys) => onColumnFiltersChange?.({ data_creation_type: keys })}
                        />
                    )}
                </ColumnFilterHeader>
            );
            width = extraParamColumnMinWidth('Data Creation', { hasFilter: true });
        } else if (title === 'dataset') {
            const selected = currentFilters.dataset || [];
            displayTitle = (
                <ColumnFilterHeader
                    title="Sample Origin"
                    active={selected.length > 0}
                    onOpenChange={(open) => onTablePopoverChange?.('sample-origin', open)}
                >
                    {({ close }) => (
                        <CheckboxFilterPanel
                            options={sampleOriginOptions}
                            selectedKeys={selected}
                            onClose={close}
                            onClear={() => onColumnFiltersChange?.({ dataset: null })}
                            onApply={(keys) => onColumnFiltersChange?.({ dataset: keys })}
                        />
                    )}
                </ColumnFilterHeader>
            );
            width = extraParamColumnMinWidth('Sample Origin', { hasFilter: true });
        } else if (title === 'dataset_card') {
            const selected = currentFilters.dataset_card || [];
            displayTitle = (
                <ColumnFilterHeader
                    title="dataset_card"
                    active={selected.length > 0}
                    onOpenChange={(open) => onTablePopoverChange?.('dataset-card', open)}
                >
                    {({ close }) => (
                        <CheckboxFilterPanel
                            options={datasetCardOptions}
                            selectedKeys={selected}
                            onClose={close}
                            onClear={() => onColumnFiltersChange?.({ dataset_card: null })}
                            onApply={(keys) => onColumnFiltersChange?.({ dataset_card: keys })}
                        />
                    )}
                </ColumnFilterHeader>
            );
            width = extraParamColumnMinWidth('dataset_card', { hasFilter: true });
        } else if (enableOriginLabelsFilter && isOriginLabelsColumn) {
            const selectedMode = originLabelsFilterKey(currentFilters.origin_labels_mismatch) === title
                ? 'mismatch'
                : originLabelsFilterKey(currentFilters.origin_labels_match) === title
                    ? 'match'
                    : null;
            const originFilter = originLabelsFilterPayload(title, isMultilabel);
            displayTitle = (
                <ColumnFilterHeader
                    title={formatOriginLabelsColumnLabel(title)}
                    active={!!selectedMode}
                    onOpenChange={(open) => onTablePopoverChange?.(`origin-labels-${title}`, open)}
                >
                    {({ close }) => (
                        <OriginLabelsFilterPanel
                            selectedKey={selectedMode}
                            onClose={close}
                            onClear={() => onColumnFiltersChange?.({
                                origin_labels_mismatch: null,
                                origin_labels_match: null,
                            })}
                            onApply={(mode) => {
                                if (mode === 'mismatch') {
                                    onColumnFiltersChange?.({
                                        origin_labels_mismatch: originFilter,
                                        origin_labels_match: null,
                                    });
                                } else if (mode === 'match') {
                                    onColumnFiltersChange?.({
                                        origin_labels_match: originFilter,
                                        origin_labels_mismatch: null,
                                    });
                                } else {
                                    onColumnFiltersChange?.({
                                        origin_labels_mismatch: null,
                                        origin_labels_match: null,
                                    });
                                }
                            }}
                        />
                    )}
                </ColumnFilterHeader>
            );
        }

        const headerLabel = title === 'data_creation_type'
            ? 'Data Creation'
            : title === 'dataset'
                ? 'Sample Origin'
                : formatOriginLabelsColumnLabel(title);
        const computedWidth = extraParamColumnMinWidth(headerLabel, { hasFilter });
        const columnWidth = typeof width === 'number' ? Math.max(width, computedWidth) : computedWidth;

        const columnKey = (title === 'data_creation_type' || title === 'dataset')
            ? title
            : `extra_param_${title}`;

        const useLongTextDisplay = longTextColumns.has(title);

        return {
            title: displayTitle,
            dataIndex: title,
            key: columnKey,
            width: columnWidth,
            minWidth: columnWidth,
            render: (text, record) => {
                if (crossRefConfig?.kind === 'entities') {
                    const entities = getExtraParamEntities(record, title);
                    if (entities) {
                        return (
                            <EntitiesPreviewCell
                                entities={entities}
                                text={record?.request_example}
                                allLabels={crossRefConfig.color_names || []}
                            />
                        );
                    }
                } else if (crossRefConfig?.kind === 'labels') {
                    const raw = getExtraParamRaw(record, title, text);
                    const labels = tokenizeLabels(raw, crossRefConfig.delimiter, crossRefConfig.is_multilabel);
                    if (labels) {
                        return <LabelsPreviewCell labels={labels} allLabels={crossRefConfig.color_names || []} />;
                    }
                } else if (isOriginLabelsColumn) {
                    const raw = getExtraParamRaw(record, title, text);
                    const labels = tokenizeLabels(raw, null, isMultilabel);
                    if (labels) {
                        return <LabelsPreviewCell labels={labels} allLabels={allLabelOptions} />;
                    }
                    if (raw !== undefined) {
                        return getExtraParamValue(record, title);
                    }
                    return '';
                }
                const value = getExtraParamValue(record, title);
                if (title === 'dataset' && typeof value === 'string') {
                    return getUserFacingDatasetName(value) || value;
                }
                if (useLongTextDisplay && typeof value === 'string') {
                    return (
                        <InputTextArea
                            value={value}
                            readOnly
                            showLabel={false}
                            showHelperText={false}
                            className="extra-param-long-text-cell"
                        />
                    );
                }
                return value;
            },
        };
    });
}

// Helper: compute and render a tooltip that shows per-type validated and waiting counts
function getValidationTooltipContent(items, getType, getIsValidated, allTypeOptions = [], emptyMessage = 'No items') {
    const validatedByLabel = countByLabel(items.filter(item => getIsValidated(item)), getType);
    const waitingByLabel = countByLabel(items.filter(item => !getIsValidated(item)), getType);

    const hasValidated = Object.keys(validatedByLabel).length > 0;
    const hasWaiting = Object.keys(waitingByLabel).length > 0;

    if (!hasValidated && !hasWaiting) return emptyMessage;

    return (
        <div>
            {hasValidated && (
                <div style={{ marginBottom: 6 }}>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>Reviewed:</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {Object.entries(validatedByLabel).map(([label, count]) => (
                            <EllipsisTagWithTooltip
                                key={`v-${label}`}
                                variant={getSparkTagVariant(label, allTypeOptions)}
                                text={`${label} (${count})`}
                                maxWidth={260}
                                noMargin
                                style={{ marginInlineEnd: 0 }}
                            />
                        ))}
                    </div>
                </div>
            )}
            {hasWaiting && (
                <div>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>To review:</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {Object.entries(waitingByLabel).map(([label, count]) => (
                            <EllipsisTagWithTooltip
                                key={`w-${label}`}
                                variant={getSparkTagVariant(label, allTypeOptions)}
                                text={`${label} (${count})`}
                                maxWidth={260}
                                noMargin
                                style={{ marginInlineEnd: 0 }}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function renderWithCountsTooltip(items, getType, getIsValidated, allTypeOptions, emptyMessage, children) {
    const tooltipContent = getValidationTooltipContent(items, getType, getIsValidated, allTypeOptions, emptyMessage);
    return (
        <TooltipTrigger text={tooltipContent} placement="top">
            {children}
        </TooltipTrigger>
    );
}

function nerColumnFilterHeader({
    title,
    filterKey,
    selectedKeys,
    optionNames,
    labelCounts,
    isBenchmark,
    searchPlaceholder,
    onColumnFiltersChange,
    onTablePopoverChange,
}) {
    return (
        <ColumnFilterHeader
            title={title}
            active={!!(selectedKeys && selectedKeys.length > 0)}
            onOpenChange={(open) => onTablePopoverChange?.(filterKey, open)}
        >
            {({ close }) => (
                <LabelsFilterPanel
                    selectedKeys={selectedKeys || []}
                    onClose={close}
                    allLabelOptions={{ label_names: optionNames || [] }}
                    isMultilabel={false}
                    labelCounts={labelCounts || {}}
                    restrictToCountedLabels={!!isBenchmark}
                    searchPlaceholder={searchPlaceholder}
                    onClear={() => onColumnFiltersChange?.({ [filterKey]: null })}
                    onApply={(keys) => onColumnFiltersChange?.({ [filterKey]: keys })}
                />
            )}
        </ColumnFilterHeader>
    );
}

export function getDatasetColumns(extra_params_titles, onSelectionChange, firstDatasetOrBenchmark, isBenchmark, taskType, updateRecord, metadata, currentPage, pageSize, llm_usecase_id, displayNames = {}, allLabelOptions = {}, labelCounts = {}, fetchValidationCounts, refreshCategories, client_id, relationshipTypes, refreshRelationshipTypes, extraProps = {}, isMultilabel = false, currentFilters = {}, onRefreshRecords = null, selectedRowKeys = [], isSelectAllActive = false, excludedIds = [], dataSource = [], onToggleAllOnPage = null) {
    const datasetForRecord = (record) =>
        resolveDatasetNameForRecord(record, extraProps.currentUsecase, isBenchmark)
        || firstDatasetOrBenchmark;
    const onDataOverviewRefresh = extraProps.onDataOverviewRefresh;

    const pageIds = dataSource.map(r => r._id).filter(Boolean);
    const checkedOnPage = isSelectAllActive
        ? pageIds.filter(id => !excludedIds.includes(id))
        : pageIds.filter(id => selectedRowKeys.includes(id));
    const allOnPageChecked = pageIds.length > 0 && checkedOnPage.length === pageIds.length;
    const someOnPageChecked = checkedOnPage.length > 0 && !allOnPageChecked;
    const headerStatus = allOnPageChecked ? 'active' : someOnPageChecked ? 'indeterminate' : 'inactive';

    let columns = [{
        title: (
            <SparkCheckbox
                label=""
                status={headerStatus}
                onChange={() => onToggleAllOnPage?.(!allOnPageChecked)}
            />
        ),
        dataIndex: 'is_in_dataset',
        width: 50,
        render: (value, record) => {
            const isChecked = isSelectAllActive
                ? !excludedIds.includes(record._id)
                : selectedRowKeys.includes(record._id);

            return (
                <SparkCheckbox
                    label=""
                    status={isChecked ? 'active' : 'inactive'}
                    onChange={(e) => onSelectionChange(record._id, e.target.checked)}
                />
            );
        }
    },];

    const optuneScore = {
        title: 'SpecificAI Score',
        dataIndex: 'score',
        // Wide enough for the uppercase header ("SPECIFICAI SCORE") not to clip.
        width: "170px",
        render(text, record) {
            const isLoading = record.score === null || record.score === undefined || record.score === DEFAULT_SCORE;
            const backgroundColor = isLoading ? "transparent"
                : (record.score >= ScoreQualityTH.HIGH) ? colors.green
                : record.score >= ScoreQualityTH.MEDIUM ? colors.orange
                : colors.red;

            return (
                <div style={{
                    background: backgroundColor,
                    color: isLoading ? undefined : 'white',
                    textAlign: 'center',
                    width: '100%',
                    padding: '4px 0',
                    borderRadius: 'var(--dimension-border-radius-04)',
                }}>
                    {isLoading
                        ? <Skeleton shapeType="circle" width={16} height={16} />
                        : record.score.toFixed(0)}
                </div>
            );
        },
    }

    const initialColumns = [];

    // Only add optuneScore column if it's explicitly requested in extra_params_titles
    if (extra_params_titles.includes('optune_score') &&
        taskType !== TaskType.NER &&
        taskType !== TaskType.NER_WITH_RELATIONS) {
        initialColumns.push(optuneScore);
    }

    const sortedEntityOptions = (allLabelOptions?.entity_names || [])?.map(l => ({ value: l, label: l, color: '#1890ff' })).sort((a, b) => a.label.localeCompare(b.label))
    columns = columns.concat(initialColumns);

    // Agentic tasks (autonomy level ≠ 1) render the trajectory inside EXAMPLE
    // and add TOOLS CALLED. Generative agentic rows follow the Figma layout
    // (Example + Response inside EXAMPLE, no separate Response column);
    // classification / NER keep their Labels / Entities / Relations columns.
    const isAgentic = isAgenticAutonomyLevel(extraProps.currentUsecase?.autonomy_level);
    const isAgenticGenerative = isAgentic && isGenerativeTask(taskType);

    columns = columns.concat([
        {
            title: 'Example',
            dataIndex: 'request_example',
            key: 'request_example',
            flex: 3,
            // Keep the flexible column from collapsing when many fixed-width
            // columns are added; the wrapper scrolls horizontally instead.
            minWidth: 240,
            render: (text, record) => {
                if (isAgenticGenerative) {
                    return (
                        <AgenticExampleCell
                            record={record}
                            exampleText={text}
                            responseText={record?.response?.response ?? ''}
                            showResponse
                        />
                    );
                }
                const exampleEditor = renderExampleEditor(text, record);
                return isAgentic ? <AgenticExampleCell record={record} header={exampleEditor} /> : exampleEditor;
            },
        },
    ]);

    function renderExampleEditor(text, record) {
                const showAiSparkle =
                    record?.data_creation_type === AI_FULLY_GENERATED_ORIGIN &&
                    !hasEditedExample(record);
                const aiEndIcon = showAiSparkle ? <AiGeneratedExampleIcon /> : undefined;
                return taskType === TaskType.NER || taskType === TaskType.NER_WITH_RELATIONS ? (
                    <div style={{ minWidth: 0 }}>
                        <NERResponseCell
                            text={text}
                            record={record}
                            entityOptions={sortedEntityOptions}
                            onChange={(record, newEntities, newRelations, activity_type, activity_comment) => {
                                record.response.entities = newEntities
                                record.response.relations = newRelations
                                updateRecordResponse(record, record.response, datasetForRecord(record), isBenchmark, updateRecord, fetchValidationCounts, llm_usecase_id, activity_type, activity_comment, taskType, onDataOverviewRefresh)
                            }}
                            datasetName={datasetForRecord(record)}
                            refreshCategories={refreshCategories}
                            isBenchmark={isBenchmark}
                            updateRecord={updateRecord}
                            fetchValidationCounts={fetchValidationCounts}
                            client_id={client_id}
                            relationshipTypes={relationshipTypes}
                            refreshRelationshipTypes={refreshRelationshipTypes}
                            llm_usecase_id={llm_usecase_id}
                            taskType={taskType}
                            onRefreshRecords={onRefreshRecords}
                            onDataOverviewRefresh={onDataOverviewRefresh}
                            endIcon={aiEndIcon}
                        />
                    </div>
                ) : (
                    <div style={{ minWidth: 0, flex: 1, display: "flex", alignSelf: "stretch" }}>
                        <EditableText
                            text={text}
                            endIcon={aiEndIcon}
                            onSave={async (newText) => {
                                if (newText.length === 0) {
                                    sparkToast.error("Example cannot be empty");
                                    throw new Error("Example cannot be empty");
                                }
                                return updateRecordExample(record, newText, datasetForRecord(record), isBenchmark, updateRecord, fetchValidationCounts, llm_usecase_id, onDataOverviewRefresh);
                            }}
                        />
                    </div>
                )
    }

    // Add filters to task-specific columns
    let taskTypeColumns = [];
    if (taskType === TaskType.classification) {
        taskTypeColumns = [
            // Only add display_name column if there are display names different from default labels
            ...(Object.keys(displayNames).some(key => displayNames[key] && displayNames[key] !== key) ? [{
                title: 'Display Names',
                dataIndex: 'display_name',
                key: 'display_name',
                width: '140px',
                render: (text, record) => {
                    // Get display names from the labels array
                    const labels = record.response.labels || [];

                    if (!labels.length) {
                        return (
                            <div style={{
                                fontWeight: 'medium',
                                color: '#8c8c8c',
                                fontStyle: 'italic',
                                background: 'linear-gradient(to right, rgba(24, 144, 255, 0.05), transparent)',
                                borderRadius: '4px',
                                padding: '8px 12px',
                                border: '1px solid rgba(255, 255, 255, 0.08)',
                                boxShadow: 'inset 0 1px 3px rgba(0, 0, 0, 0.1)',
                            }}>
                                No labels
                            </div>
                        );
                    }

                    return (
                        <div style={{ display: 'flex', flexDirection: 'row', gap: '4px', flexWrap: 'wrap' }}>
                            {labels.map((label, index) => {
                                const hasDisplayName = displayNames[label] && displayNames[label] !== label;
                                const displayName = hasDisplayName ? displayNames[label] : label;

                                return (
                                    <TagColored
                                        key={`${label}-${index}`}
                                        variant={getSparkTagVariant(label, allLabelOptions.label_names || [])}
                                        text={displayName}
                                    />
                                );
                            })}
                        </div>
                    );
                },
            }] : []),
            {
                title: (
                    <ColumnFilterHeader
                        title={
                            isMultilabel && currentFilters.labels && currentFilters.labels.length > 0 ? (
                                <TooltipTrigger text={`${currentFilters.labels.length} label${currentFilters.labels.length > 1 ? 's' : ''} filtered`}>
                                    <span>Labels</span>
                                </TooltipTrigger>
                            ) : (
                                'Labels'
                            )
                        }
                        label="Labels"
                        active={!!(currentFilters.labels && currentFilters.labels.length > 0)}
                        onOpenChange={(open) => extraProps.onTablePopoverChange?.('labels', open)}
                    >
                        {({ close }) => (
                            <LabelsFilterPanel
                                selectedKeys={currentFilters.labels || []}
                                onClose={close}
                                allLabelOptions={allLabelOptions}
                                isMultilabel={isMultilabel}
                                labelCounts={labelCounts}
                                restrictToCountedLabels={!!isBenchmark}
                                unlabeledCount={extraProps.unlabeledCount || 0}
                                noneSelectedCount={extraProps.noneSelectedCount || 0}
                                onPanelOpen={extraProps.onLabelsPanelOpen}
                                onClear={() => extraProps.onColumnFiltersChange?.({ labels: null })}
                                onApply={(keys) => extraProps.onColumnFiltersChange?.({ labels: keys })}
                            />
                        )}
                    </ColumnFilterHeader>
                ),
                dataIndex: 'labels',
                minWidth: 190,
                render: (text, record) => {
                    const labels = record.response?.labels || [];
                    // Create a key based on record ID and first label to force re-render when labels change
                    // Use the first label (or empty string) to ensure key changes when label is replaced
                    const firstLabel = labels.length > 0 ? labels[0] : '';
                    const labelKey = `${record._id}-${firstLabel}`;
                    const isAiOrigin =
                        record?.response?.response_origin === AI_FULLY_GENERATED_ORIGIN ||
                        record?.response?.response_origin === AI_LABELED_ORIGIN;
                    // Single-label: drop the sparkle once a human replaced the AI
                    // prediction. Multi-label has no per-tag original set, so every
                    // current tag on an AI-origin row keeps the sparkle.
                    const showAiSparkle = isAiOrigin && (
                        isMultilabel
                        || !isAiLabelOverridden(record, allLabelOptions.label_names || [], firstLabel)
                    );

                    return (
                        <div key={labelKey} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                            <LabelSelector
                                key={labelKey}
                                labels={labels}
                                onSave={(newLabels) => {
                                    // For single-label classification, update frontend immediately
                                    if (taskType === TaskType.classification && !isMultilabel) {
                                        const updatedRecord = {
                                            ...record,
                                            response: {
                                                ...record.response,
                                                labels: newLabels
                                            }
                                        };
                                        updateRecord(updatedRecord, isBenchmark);
                                    }

                                    // Call updateRecordLabels in parallel
                                    return updateRecordLabels(record, newLabels, datasetForRecord(record), isBenchmark, updateRecord, llm_usecase_id, fetchValidationCounts, onDataOverviewRefresh);
                                }}
                                allLabelOptions={allLabelOptions.label_names || []}
                                taskType={taskType}
                                isMultilabel={isMultilabel}
                                record={record}
                                showSparkleInTag={showAiSparkle}
                                onDropdownVisibleChange={(open) => extraProps.onTablePopoverChange?.(`label-${record._id}`, open)}
                            />
                        </div>
                    );
                },
                width: '200px',
            },
        ]
    } else if (isGenerativeTask(taskType) && !isAgenticGenerative) {
        const outputNoun = getGenerativeOutputNoun(taskType);
        const outputTitle = outputNoun.charAt(0).toUpperCase() + outputNoun.slice(1);
        taskTypeColumns.push({
            title: outputTitle,
            dataIndex: 'response',
            key: 'response',
            width: '30%',
            render: (text, record) => {
                const summaryText = record?.response?.response || `No ${outputNoun}`;
                return (
                    <EditableText
                        text={summaryText}
                        onSave={async (newText) => {
                            if (newText.trim().length === 0) {
                                sparkToast.error(`${outputTitle} cannot be empty`);
                                throw new Error(`${outputTitle} cannot be empty`);
                            }
                            const newResponse = { ...record.response, response: newText };
                            return updateRecordResponse(
                                record,
                                newResponse,
                                datasetForRecord(record),
                                isBenchmark,
                                updateRecord,
                                fetchValidationCounts,
                                llm_usecase_id,
                                ActivityType.summary_changed,
                                `${outputTitle} changed`,
                                taskType,
                                onDataOverviewRefresh
                            );
                        }}
                    />
                );
            },
        });
    }

    columns = columns.concat(taskTypeColumns);

    const filteredExtraParamsTitles = extra_params_titles.filter(title => title !== 'optune_score');
    const extraParamsColumns = createExtraParamsColumns(
        filteredExtraParamsTitles,
        extraProps.crossReferenceFields || {},
        dataSource,
        allLabelOptions?.label_names || [],
        isMultilabel,
        currentFilters,
        extraProps.datasetNames || [],
        extraProps.onColumnFiltersChange,
        taskType === TaskType.classification && !isMultilabel,
        extraProps.onTablePopoverChange,
        extraProps.datasetCardFilterValues || [],
    );
    columns = columns.concat(extraParamsColumns);



    // Add action columns for NER responses
    if (taskType === TaskType.NER || taskType === TaskType.NER_WITH_RELATIONS) {
        columns.push({
            title: nerColumnFilterHeader({
                title: 'Entities To Review',
                filterKey: 'entities',
                selectedKeys: currentFilters.entities,
                optionNames: allLabelOptions?.entity_names || [],
                labelCounts: extraProps.entityCounts || {},
                isBenchmark,
                searchPlaceholder: 'Search all entities',
                onColumnFiltersChange: extraProps.onColumnFiltersChange,
                onTablePopoverChange: extraProps.onTablePopoverChange,
            }),
            key: 'details',
            width: '300px',
            render: (text, record) => {
                const entities = record?.response?.entities || [];
                const unvalidated = entities.filter(e => !e.is_validated);
                const counts = countByLabel(unvalidated, e => e.label || 'UNKNOWN');
                const tags = Object.keys(counts).length === 0
                    ? <span style={{ color: 'var(--Semantic-Content-secondary)', fontStyle: 'italic' }}>All reviewed</span>
                    : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-start' }}>
                            {Object.entries(counts).map(([label, count]) => (
                                <TagColored
                                    key={label}
                                    label={`${label} (${count})`}
                                    variant={getSparkTagVariant(label, allLabelOptions?.entity_names || [])}
                                />
                            ))}
                        </div>
                    );
                return tags;
            },
        });
    }
    // Add action columns for NER responses
    if (taskType === TaskType.NER_WITH_RELATIONS) {
        // Validate All column
        columns.push({
            title: nerColumnFilterHeader({
                title: 'Relations',
                filterKey: 'relations',
                selectedKeys: currentFilters.relations,
                optionNames: allLabelOptions?.relation_names || relationshipTypes || [],
                labelCounts: extraProps.relationCounts || {},
                isBenchmark,
                searchPlaceholder: 'Search all relations',
                onColumnFiltersChange: extraProps.onColumnFiltersChange,
                onTablePopoverChange: extraProps.onTablePopoverChange,
            }),
            key: 'relations',
            width: '150px',
            render: (text, record) => {
                const relations = record?.response?.relations || [];
                const countText = relations.length === 1 ? '1 relation' : `${relations.length} relations`;
                return renderWithCountsTooltip(
                    relations,
                    r => r.label || 'UNKNOWN',
                    r => !!r.is_validated,
                    relationshipTypes || [],
                    'No relations',
                    (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                            <ValidationDetails
                                entities={relations}
                                isRelations={true}
                                onValidateAll={() => {
                                    validateAllEntities(record, datasetForRecord(record), isBenchmark, updateRecord, fetchValidationCounts, llm_usecase_id, taskType);
                                }}
                            />
                        </div>
                    )
                );
            },
        });
    }

    if (isAgentic) {
        const selectedTools = currentFilters.tools_called || [];
        // Distinct tool names across the tab (server-side, sorted); the checked
        // ones are kept even when they no longer occur so an applied filter stays
        // visible. The same sorted list fixes each tool's color, so a tool looks
        // the same in every TOOLS CALLED row and in the filter menu.
        const toolNames = tabToolNames(extraProps.toolCallFilterValues, selectedTools);
        const toolVariantByName = toolTagVariantsByName(toolNames);
        columns.push({
            title: (
                <ColumnFilterHeader
                    title="Tools Called"
                    active={selectedTools.length > 0}
                    onOpenChange={(open) => extraProps.onTablePopoverChange?.('tools-called', open)}
                >
                    {({ close }) => (
                        <ToolsCalledFilterPanel
                            toolNames={toolNames}
                            variantByName={toolVariantByName}
                            selectedKeys={selectedTools}
                            onClose={close}
                            onClear={() => extraProps.onColumnFiltersChange?.({ tools_called: null })}
                            onApply={(keys) => extraProps.onColumnFiltersChange?.({ tools_called: keys })}
                        />
                    )}
                </ColumnFilterHeader>
            ),
            dataIndex: 'trajectory',
            key: 'tools_called',
            width: '238px',
            render: (_value, record) => <ToolsCalledCell record={record} variantByName={toolVariantByName} />,
        });
    }

    // Add common columns for all task types
    columns.push(createReviewedColumn(
        client_id,
        firstDatasetOrBenchmark,
        isBenchmark,
        llm_usecase_id,
        updateRecord,
        fetchValidationCounts,
        currentFilters,
        extraProps.currentUsecase,
        extraProps.onColumnFiltersChange,
        extraProps.onTablePopoverChange,
        onDataOverviewRefresh,
    ));

    if (taskType === TaskType.NER || taskType === TaskType.NER_WITH_RELATIONS) {
        columns.push(createViewColumn(extraProps));
    }

    // "..." actions column — always rightmost
    columns.push(createMoreActionsColumn(
        client_id,
        firstDatasetOrBenchmark,
        isBenchmark,
        llm_usecase_id,
        updateRecord,
        fetchValidationCounts,
        currentFilters,
        onRefreshRecords,
        onDataOverviewRefresh,
        taskType
    ));

    return columns;
}

function renderQuality(text, backgroundColor, isLoading) {
    return {
        props: {
            style: {
                background: backgroundColor,
                color: "white",
                padding: 0,
                margin: 0,
                textAlign: 'center',
                verticalAlign: 'middle',
                height: '100%'
            }
        },
        children: isLoading ?
            <span><Skeleton shapeType="rectangle" width={14} height={14} style={{ display: 'inline-block', marginRight: 5, verticalAlign: 'middle' }} /> {text}</span> :
            text
    };
}
export function getProcessedDataColumns(isLoading) {
    return [
        {
            title: '# Raw Entries',
            dataIndex: 'num_rows_entries',
        },
        // {
        //     title: '# Rows After Dedup',
        //     dataIndex: 'after_dedup',
        // },
        // {
        //     title: '# Rows changed due to PII',
        //     dataIndex: 'num_changes_pii',
        // },
        // {
        //     title: '# Rows changed due to Decontamination',
        //     dataIndex: 'num_changes_decontamination',
        // },
        {
            title: '# Great Quality',
            dataIndex: 'num_greens',
            render: (text) => renderQuality(text, colors.green, isLoading),
            width: "150px",
        },
        {
            title: '# Medium Quality',
            dataIndex: 'num_oranges',
            render: (text) => renderQuality(text, colors.orange, isLoading),
            width: "150px",
        },
        {
            title: '# Low Quality',
            dataIndex: 'num_reds',
            render: (text) => renderQuality(text, colors.red, isLoading),
            width: "150px",
        },
    ]
}

// export const processedDataInfo = [
//     {
//         key: 1,
//         num_rows_entries: 1269532,
//         after_dedup: 726953,
//         num_changes_pii: 520,
//         num_changes_decontamination: 1370,
//         num_greens: 528867,
//         num_oranges: 120435,
//         num_reds: 77651,
//     },
// ]

// export const improvedProcessedDataInfo = [
//     {
//         ...processedDataInfo[0],
//         num_greens: 568484,
//         num_oranges: 96348,
//         num_reds: 62121,
//     },
// ]